import {inject, Injectable} from '@angular/core';
import {combineLatest, firstValueFrom, Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {VaultMember} from '../api/models/vault-member';
import {MemberStore} from '../stores/member.store';
import {distinctUntilChangedObject} from '../util/rxjs-customs/distinct-until-changed-object.util';
import {UserGroupQuery} from './user-group.query';
import {EntityQueryService} from '../modules/akita-mimic/services/entity-query.service';
import {MemberState} from '../modules/akita-mimic/models/store-states/member-state.model';
import {SortOrder} from '../modules/akita-mimic/enums/sort-order.enum';

@Injectable({ providedIn: 'root' })
export class MemberQuery extends EntityQueryService<MemberState, VaultMember> {
    public activeMember$: Observable<VaultMember | undefined> = this.selectFirstActive();
    public isLoading$: Observable<boolean> = this.selectLoading();
    private readonly members$: Observable<Array<VaultMember>> = this.selectAll();
    private readonly userGroupQuery = inject(UserGroupQuery);
    private readonly vaultMemberWithoutGroup$ = combineLatest([
        this.userGroupQuery.userGroupMember$,
        this.members$,
    ])
        .pipe(
            distinctUntilChangedObject(),
            map(([groupMembers, vaultMembers]) => {
                return vaultMembers.filter(member => {
                    return groupMembers.findIndex(m => m.userId === member.userId) === -1;
                });
            })
        );

    public constructor() {
        const store = inject(MemberStore);
        super(store, {
            sortBy: 'userFullName',
            sortByOrder: SortOrder.ASC
        });
    }

    public getMemberByUserId(userId: string): VaultMember | undefined {
        const entities = this.getValue().entities;
        if (entities) {
            return Object.values(entities)
                .find(member => member.userId === userId);
        }
        return undefined;
    }

    public async getPaginatedMembers(offset: number, limit: number): Promise<Array<VaultMember>> {
        const storeGroups = await firstValueFrom(this.members$.pipe(take(1)));
        let groups: Array<VaultMember> = [];
        if (storeGroups) {
            groups = storeGroups.slice(offset, offset + limit);
        }
        return groups;
    }

    public async getUnassignedMemberLength(): Promise<number> {
        const memberList = await firstValueFrom(this.vaultMemberWithoutGroup$);
        return memberList.length;
    }

    public async getPaginatedUnassignedMembers(offset: number, limit: number): Promise<Array<VaultMember>> {
        const storeGroups = await firstValueFrom(this.vaultMemberWithoutGroup$.pipe(take(1)));
        let groups: Array<VaultMember> = [];
        if (storeGroups) {
            groups = storeGroups.slice(offset, offset + limit);
        }
        return groups;
    }
}
